import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useDrag, useDrop } from 'react-dnd'
import { ExperienceInputField } from '../form'
import QuestHOC from './QuestHOC'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'
import ExperienceButton from './ExperienceButton'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const truncateSectionTitle = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

export const TESTIMONIAL_ROW_DND = 'TESTIMONIAL_ROW'

const emptyVideos = () => [
  { title: '', youtubeLink: '' },
  { title: '', youtubeLink: '' },
  { title: '', youtubeLink: '' },
]

const emptySection = () => ({
  title: '',
  videos: emptyVideos(),
})

const YOUTUBE_URL_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/

const sectionSchema = Yup.object().shape({
  title: Yup.string(),
  videos: Yup.array().of(
    Yup.object().shape({
      title: Yup.string(),
      youtubeLink: Yup.string().test(
        'youtube-url',
        'Invalid YouTube URL',
        (value) => !value?.trim() || YOUTUBE_URL_REGEX.test(value),
      ),
    }),
  ),
})

const padVideos = (videos = []) => {
  const next = [...videos]
  while (next.length < 3) {
    next.push({ title: '', youtubeLink: '' })
  }
  return next.slice(0, 3).map((v) => ({
    title: v?.title || '',
    youtubeLink: v?.youtubeLink || '',
  }))
}

const mapFromQuest = (questDetail) =>
  ['firstRow', 'secondRow', 'thirdRow'].map((rowKey) => {
    const row = questDetail?.testimonialVideos?.[rowKey]
    return {
      title: row?.title || '',
      videos: padVideos(row?.videos),
    }
  })

const sectionHasContent = (section) => {
  if (section?.title?.trim()) return true
  return (section?.videos || []).some(
    (v) => v?.title?.trim() || v?.youtubeLink?.trim(),
  )
}

const videoSummary = (section) => {
  const filled = (section?.videos || []).filter(
    (v) => v?.title?.trim() || v?.youtubeLink?.trim(),
  )
  if (!filled.length) return 'No videos yet'
  const titles = filled
    .map((v) => v.title?.trim())
    .filter(Boolean)
    .slice(0, 2)
  const countLabel = `${filled.length} video${filled.length === 1 ? '' : 's'}`
  return titles.length ? `${countLabel} · ${titles.join(', ')}` : countLabel
}

const toApiPayload = (sections) => {
  const rows = ['firstRow', 'secondRow', 'thirdRow']
  return sections.slice(0, 3).reduce((acc, section, idx) => {
    acc[rows[idx]] = {
      title: section.title || '',
      videos: padVideos(section.videos),
    }
    return acc
  }, {})
}

const QuestDetailTestimonials = ({
  questDetail,
  updateQuestDetails,
  isFormSubmitting,
}) => {
  const [sections, setSections] = useState(() => mapFromQuest(questDetail))
  const sectionsRef = useRef(sections)
  const [editingIndex, setEditingIndex] = useState(null) // number | 'new' | null
  const [draft, setDraft] = useState(emptySection())
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    index: null,
    sectionTitle: '',
  })

  useEffect(() => {
    sectionsRef.current = sections
  }, [sections])

  useEffect(() => {
    setSections(mapFromQuest(questDetail))
  }, [questDetail])

  useEffect(() => {
    if (editingIndex === null) {
      return undefined
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeEditor()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [editingIndex])

  const visibleSections = useMemo(
    () =>
      sections
        .map((section, index) => ({ section, index }))
        .filter(({ section }) => sectionHasContent(section)),
    [sections],
  )

  const persist = (nextSections, options = {}) => {
    const padded = ['firstRow', 'secondRow', 'thirdRow'].map((_, idx) => {
      const found = nextSections[idx]
      return found
        ? { title: found.title || '', videos: padVideos(found.videos) }
        : emptySection()
    })
    setSections(padded)
    updateQuestDetails(
      { testimonialVideos: toApiPayload(padded) },
      options.silent ? { silent: true } : undefined,
    )
  }

  const openEdit = (index) => {
    if (editingIndex === index) {
      closeEditor()
      return
    }
    setEditingIndex(index)
    setDraft({
      title: sections[index]?.title || '',
      videos: padVideos(sections[index]?.videos),
    })
  }

  const openAdd = () => {
    const emptyIdx = sections.findIndex((s) => !sectionHasContent(s))
    if (emptyIdx < 0) return
    setEditingIndex('new')
    setDraft(emptySection())
  }

  const closeEditor = () => {
    setEditingIndex(null)
    setDraft(emptySection())
  }

  const handleSectionSave = (values) => {
    const next = sections.map((s) => ({
      title: s.title || '',
      videos: padVideos(s.videos),
    }))
    if (editingIndex === 'new') {
      if (!sectionHasContent(values)) {
        closeEditor()
        return
      }
      const emptyIdx = next.findIndex((s) => !sectionHasContent(s))
      if (emptyIdx >= 0) {
        next[emptyIdx] = values
        persist(next)
      }
    } else if (typeof editingIndex === 'number') {
      next[editingIndex] = values
      persist(next)
    }
    closeEditor()
  }

  const requestRemoveAt = (index) => {
    const section = sections[index]
    setDeleteModal({
      show: true,
      index,
      sectionTitle: section?.title?.trim() || 'Untitled section',
    })
  }

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, index: null, sectionTitle: '' })
  }

  const confirmRemoveAt = () => {
    const { index } = deleteModal
    if (typeof index !== 'number') {
      closeDeleteModal()
      return
    }
    const next = sections.map((s, i) =>
      i === index ? emptySection() : { ...s, videos: padVideos(s.videos) },
    )
    persist(next)
    if (editingIndex === index) closeEditor()
    closeDeleteModal()
  }

  const moveItem = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return
    const next = sectionsRef.current.map((s) => ({
      title: s.title || '',
      videos: padVideos(s.videos),
    }))
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)
    sectionsRef.current = next
    setSections(next)
    if (editingIndex === fromIndex) {
      setEditingIndex(toIndex)
    } else if (
      typeof editingIndex === 'number' &&
      fromIndex < editingIndex &&
      toIndex >= editingIndex
    ) {
      setEditingIndex(editingIndex - 1)
    } else if (
      typeof editingIndex === 'number' &&
      fromIndex > editingIndex &&
      toIndex <= editingIndex
    ) {
      setEditingIndex(editingIndex + 1)
    }
  }

  const persistAfterReorder = () => {
    const padded = ['firstRow', 'secondRow', 'thirdRow'].map((_, idx) => {
      const found = sectionsRef.current[idx]
      return found
        ? { title: found.title || '', videos: padVideos(found.videos) }
        : emptySection()
    })
    updateQuestDetails(
      { testimonialVideos: toApiPayload(padded) },
      { silent: true },
    )
  }

  const canAdd = visibleSections.length < 3 && editingIndex === null

  const deleteSectionName = truncateSectionTitle(deleteModal.sectionTitle)
  const deleteSectionMessage = deleteSectionName
    ? `\u201C${deleteSectionName}\u201D will be removed. It will be removed from the Prosperity page.`
    : 'It will be removed from the Prosperity page.'

  return (
    <div>
      <p className='experience-f-hint-top text-dark'>
        Up to three sections. Each can hold up to three videos — you do not need
        to fill all three.
      </p>

      <div className='experience-lrow-list' data-add-list='testimonials'>
        {visibleSections.map(({ section, index }) => (
          <React.Fragment key={index}>
            <TestimonialRow
              index={index}
              section={section}
              isEditing={editingIndex === index}
              onEdit={openEdit}
              onDelete={requestRemoveAt}
              moveItem={moveItem}
              onDragEnd={persistAfterReorder}
            />
            {editingIndex === index ? (
              <TestimonialInlineEditor
                initial={draft}
                onSave={handleSectionSave}
                onCancel={closeEditor}
                isSaving={isFormSubmitting}
              />
            ) : null}
          </React.Fragment>
        ))}
      </div>

      {editingIndex === 'new' ? (
        <TestimonialInlineEditor
          initial={draft}
          onSave={handleSectionSave}
          onCancel={closeEditor}
          isSaving={isFormSubmitting}
        />
      ) : null}

      {canAdd ? (
        <div className='experience-form-actions'>
          <ExperienceButton variant='outline' size='sm' onClick={openAdd}>
            Add New Section
          </ExperienceButton>
        </div>
      ) : null}

      <ExperienceConfirmModal
        show={deleteModal.show}
        onHide={closeDeleteModal}
        onConfirm={confirmRemoveAt}
        title='Delete this video section?'
        message={deleteSectionMessage}
        confirmLabel='Delete'
        isLoading={isFormSubmitting}
      />
    </div>
  )
}

const TestimonialRow = ({
  index,
  section,
  isEditing,
  onEdit,
  onDelete,
  moveItem,
  onDragEnd,
}) => {
  const ref = useRef(null)
  const [{ handlerId }, drop] = useDrop({
    accept: TESTIMONIAL_ROW_DND,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover(item) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })
  const [{ isDragging }, drag, preview] = useDrag({
    type: TESTIMONIAL_ROW_DND,
    item: () => ({ index }),
    end() {
      if (typeof onDragEnd === 'function') {
        onDragEnd()
      }
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })
  preview(drop(ref))

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={`experience-lrow${isEditing ? ' is-editing' : ''}${
        isDragging ? ' is-dragging' : ''
      }`}
      role='button'
      tabIndex={0}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      onClick={() => onEdit(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onEdit(index)
        }
      }}>
      <span
        className='experience-grab'
        title='Drag to reorder'
        ref={drag}
        onClick={(e) => e.stopPropagation()}>
        ⋮⋮
      </span>
      <span className='experience-lrow-copy'>
        <b>{section.title?.trim() || 'Untitled section'}</b>
        <small>{videoSummary(section)}</small>
      </span>
      <button
        type='button'
        className='experience-x'
        title='Delete'
        aria-label='Delete'
        onClick={(e) => {
          e.stopPropagation()
          onDelete(index)
        }}>
        <ExperienceDeleteIcon />
      </button>
    </div>
  )
}

const TestimonialInlineEditor = ({
  initial,
  onSave,
  onCancel,
  isSaving = false,
}) => (
  <div className='experience-add-form'>
    <Formik
      initialValues={initial}
      enableReinitialize
      validationSchema={sectionSchema}
      onSubmit={(values) => onSave(values)}>
      {(formik) => {
        const { handleSubmit, values, isValid } = formik
        return (
          <form onSubmit={handleSubmit}>
            <div className='experience-testimonial-editor'>
              <div className='experience-f'>
                <ExperienceInputField
                  name='title'
                  label='Section title'
                  placeholder='Add section title'
                  capitalizeFirstLetter
                  showErrorMessage={false}
                  displayFlex={false}
                />
              </div>
              <div className='experience-testimonial-slots'>
                {(values.videos || emptyVideos()).map((_, idx) => (
                  <div key={idx} className='experience-testimonial-slot'>
                    <h5>{`Entry ${idx + 1}`}</h5>
                    <div className='experience-f'>
                      <ExperienceInputField
                        name={`videos[${idx}].title`}
                        label='Title'
                        placeholder='Add title'
                        capitalizeFirstLetter
                        showErrorMessage={false}
                        displayFlex={false}
                      />
                    </div>
                    <div className='experience-f mt-15px'>
                      <ExperienceInputField
                        name={`videos[${idx}].youtubeLink`}
                        label='YouTube link'
                        placeholder='https://www.youtube.com/watch?v='
                        showErrorMessage
                        displayFlex={false}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className='experience-form-actions'>
              <ExperienceButton
                type='submit'
                variant='primary'
                size='sm'
                isLoading={isSaving}
                disabled={isSaving || !isValid}>
                Save section
              </ExperienceButton>
              <ExperienceButton
                type='button'
                variant='outline'
                size='sm'
                onClick={onCancel}
                disabled={isSaving}>
                Cancel
              </ExperienceButton>
            </div>
          </form>
        )
      }}
    </Formik>
  </div>
)

export default QuestHOC(QuestDetailTestimonials)
